import '@fontsource-variable/bricolage-grotesque';
import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';
import './styles/app.css';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ApiError } from './api/client';
import { forgetShareLinksOnDevice } from './api/hooks';
import { App } from './App';
import { AuthGate } from './components/AuthGate';
import { CrashScreen, ErrorBoundary } from './components/ErrorBoundary';
import { captureSharedLink } from './lib/openedShareLink';
import { applyTheme, readTheme } from './lib/theme';
import { UIProvider } from './lib/ui';

applyTheme(readTheme());
// A shared recipe link's token leaves the address bar before anything renders (or signs in).
captureSharedLink();

// A 401 means the session ended (signed out elsewhere, expired): re-check it to show sign-in,
// and leave no share links on this device for whoever signs in next.
function onApiError(err: unknown) {
  if (!(err instanceof ApiError && err.status === 401)) return;
  forgetShareLinksOnDevice();
  void queryClient.invalidateQueries({ queryKey: ['session'] });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary fallback={(error) => <CrashScreen error={error} />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <UIProvider>
            <AuthGate>
              <App />
            </AuthGate>
          </UIProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
